import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import ConfirmDialog from "../../components/ConfirmDialog";

export default function RolesAdminList() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editItem, setEdit]       = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm]           = useState({ name:"", slug:"" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/roles");
      setRows(res.data?.roles || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEdit(null);
    setForm({ name:"", slug:"" });
    setModal(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({ name:row.name, slug:row.slug });
    setModal(true);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }
    try {
      if (editItem) {
        await api.put(`/admin/roles/${editItem.role_id}`, form);
        toast.success("Role updated");
      } else {
        await api.post("/admin/roles", form);
        toast.success("Role created");
      }
      setModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/roles/${confirmId}`);
      toast.success("Role deleted");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Roles</h1>
          <p className="text-sm text-gray-600 mt-1">Manage admin roles and access levels</p>
        </div>
        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start"
          onClick={openAdd}
        >
          + Add Role
        </button>
      </div>

      {/* Table */}
      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">
          Roles List
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No roles found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((r,index_id) => (
                <tr key={r.role_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500">{index_id + 1}</td>
                  <td className="px-4 py-3 font-semibold">{r.name}</td>
                  <td className="px-4 py-3">
                    <code className="bg-gray-100 rounded px-1 py-0.5 text-xs">{r.slug}</code>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                      r.is_system ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"
                    }`}>
                      {r.is_system ? "System" : "Custom"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {!r.is_system ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                          onClick={() => openEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-red-50 text-red-600 border-red-200"
                          onClick={() => setConfirmId(r.role_id)}
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Protected</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-[420px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-bold text-lg">{editItem ? "Edit Role" : "Add Role"}</h2>
              <button type="button" className="text-gray-400 hover:text-gray-600 text-xl" onClick={() => setModal(false)}>×</button>
            </div>

            <form onSubmit={onSubmit} className="p-5 space-y-4">
              <div>
                <label className="text-sm font-semibold">Role Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">Slug</label>
                <input
                  name="slug"
                  value={form.slug}
                  onChange={onChange}
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="submit"
                  className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
                >
                  {editItem ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                  onClick={() => setModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmId && (
        <ConfirmDialog
          message="Are you sure you want to delete this role?"
          onConfirm={remove}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  );
}